import React, { useContext, useState, useEffect, useMemo, useCallback, lazy, Suspense } from "react";
import {
  Paper,
  Typography,
  Button,
  Grid,
  Container,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Fade,
  useMediaQuery
} from "@material-ui/core";
import Skeleton from '@material-ui/lab/Skeleton';
import { makeStyles } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import { Group as Groups } from "@material-ui/icons";

import {
  Call as CallIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  GroupAdd as GroupAddIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CheckCircle as CheckCircleIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Send as SendIcon,
  Message as MessageIcon,
  AccessAlarm as AccessAlarmIcon,
  Timer as TimerIcon,
  ArrowDownward,
  ArrowUpward,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  Loop as LoopIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon
} from "@material-ui/icons";
import * as XLSX from "xlsx";
import toastUtils from "../../utils/toastUtils";

import MainContainer from "../../components/MainContainer";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";
import { isArray } from "lodash";

import { AuthContext } from "../../context/Auth/AuthContext";

import useDashboard from "../../hooks/useDashboard";
import useContacts from "../../hooks/useContacts";
import useMessages from "../../hooks/useMessages";
import Filters from "./Filters";
import { isEmpty } from "lodash";
import moment from "moment";
import { i18n } from "../../translate/i18n";
import ForbiddenPage from "../../components/ForbiddenPage";
import ErrorBoundary from "../../components/ErrorBoundary";
import CompaniesList from "../../components/Dashboard/CompaniesList";
// import ServerMetricsCard from "../../components/Dashboard/ServerMetricsCard"; // Comentado temporariamente para diagnóstico

// Lazy load chart components for better performance
const ChatsUser = lazy(() => import("./ChartsUser").then(module => ({ default: module.ChatsUser })));
const ChartsTimeline = lazy(() => import("./ChartsTimeline").then(module => ({ default: module.ChartsTimeline })));
const ChartsPieStatus = lazy(() => import("./ChartsPieStatus").then(module => ({ default: module.ChartsPieStatus })));
const ChartsNPSGauge = lazy(() => import("./ChartsNPSGauge").then(module => ({ default: module.ChartsNPSGauge })));
const ChartsChannels = lazy(() => import("./ChartsChannels").then(module => ({ default: module.ChartsChannels })));
const ChartsDate = lazy(() => import("./ChartsDate").then(module => ({ default: module.ChartsDate })));

const useStyles = makeStyles((theme) => ({
  // Root container
  root: {
    minHeight: 'auto', // Mudado de '100vh' para 'auto'
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2),
    },
  },
  
  // Header styles
  pageHeader: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  
  pageTitle: {
    fontWeight: 700,
    fontSize: '1.75rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  
  pageSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontWeight: 400,
  },
  // Layout containers
  container: {
    padding: theme.spacing(1),
    maxWidth: '100%',
    margin: 0,
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2),
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: 1200,
      margin: '0 auto',
    },
  },
  
  section: {
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(3),
    },
  },
  
  // Filter section
  filterSection: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  
  filterButton: {
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    fontWeight: 500,
    padding: theme.spacing(1, 2),
    minWidth: 'auto',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.75, 1.5),
      fontSize: '0.875rem',
    },
  },
  // Section headers
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
  },
  
  sectionTitle: {
    fontSize: '1.375rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem',
    },
  },
  
  // Main paper
  mainPaper: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    flex: 1,
  },
  
  // Paper style for indicator cards
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    borderRadius: theme.spacing(1),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s ease-in-out',
    height: '100%',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
    },
  },
  
  // Modern card styles
  modernCard: {
    background: theme.palette.background.paper,
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    transition: 'all 0.2s ease-in-out',
    height: '100%',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-1px)',
    },
  },
  
  cardContent: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
      '&:last-child': {
        paddingBottom: theme.spacing(1.5),
      },
    },
  },
  
  // Metric card styles
  metricCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      textAlign: 'center',
      gap: theme.spacing(1),
    },
  },
  
  metricIcon: {
    width: 48,
    height: 48,
    borderRadius: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      width: 40,
      height: 40,
    },
  },
  
  metricContent: {
    flex: 1,
    minWidth: 0,
  },
  
  metricValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
    marginBottom: theme.spacing(0.25),
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem',
    },
  },
  
  metricLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8125rem',
    },
  },
  
  // Progress bars
  progressContainer: {
    marginBottom: theme.spacing(1.5),
  },
  
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  
  progressLabel: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  
  progressValue: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.action.hover,
  },
  
  // Chart container
  chartContainer: {
    background: theme.palette.background.paper,
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
  },
  
  // Loading states
  loadingCard: {
    padding: theme.spacing(2),
  },
  
  // Mobile responsive helpers
  mobileHidden: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  
  mobileOnly: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  
  // Utility classes
  centerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  
  spaceBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

// Chart loading fallback
const ChartLoadingFallback = () => {
  const classes = useStyles();
  return (
    <Box className={classes.loadingCard}>
      <Skeleton variant="text" width="40%" height={30} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 1 }} />
    </Box>
  );
};

// Modern metric card component
const MetricCard = React.memo(({ icon, value, label, color = '#2196F3', trend, loading = false }) => {
  const classes = useStyles();
  const theme = useTheme();
  
  if (loading) {
    return (
      <Card className={classes.modernCard}>
        <CardContent className={classes.cardContent}>
          <Box className={classes.metricCard}>
            <Skeleton variant="rectangular" width={48} height={48} sx={{ borderRadius: 1 }} />
            <Box className={classes.metricContent}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="text" width="80%" height={20} />
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Fade in={true} timeout={300}>
      <Card className={classes.modernCard}>
        <CardContent className={classes.cardContent}>
          <Box className={classes.metricCard}>
            <Box 
              className={classes.metricIcon} 
              sx={{ 
                backgroundColor: `${color}15`,
                color: color,
              }}
            >
              {React.cloneElement(icon, { fontSize: 'medium' })}
            </Box>
            <Box className={classes.metricContent}>
              <Typography className={classes.metricValue}>
                {typeof value === 'number' ? value.toLocaleString() : value}
              </Typography>
              <Typography className={classes.metricLabel}>
                {label}
              </Typography>
            </Box>
            {trend && (
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {trend > 0 ? (
                  <TrendingUpIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                ) : (
                  <TrendingDownIcon sx={{ color: '#F44336', fontSize: 20 }} />
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );
});

// Progress metric card component
const ProgressCard = React.memo(({ label, value, maxValue = 100, color = '#2196F3', loading = false }) => {
  const classes = useStyles();
  
  if (loading) {
    return (
      <Card className={classes.modernCard}>
        <CardContent className={classes.cardContent}>
          <Skeleton variant="text" width="70%" height={20} />
          <Box sx={{ mt: 1 }}>
            <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 1 }} />
          </Box>
          <Skeleton variant="text" width="30%" height={20} sx={{ mt: 0.5 }} />
        </CardContent>
      </Card>
    );
  }
  
  const percentage = (value / maxValue) * 100;
  
  return (
    <Fade in={true} timeout={300}>
      <Card className={classes.modernCard}>
        <CardContent className={classes.cardContent}>
          <Box className={classes.progressContainer}>
            <Box className={classes.progressHeader}>
              <Typography className={classes.progressLabel}>{label}</Typography>
              <Typography className={classes.progressValue}>{percentage.toFixed(1)}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percentage}
              className={classes.progressBar}
              sx={{
                '& .MuiLinearProgress-bar': {
                  backgroundColor: color,
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );
});

const Dashboard = () => {
  const theme = useTheme();
  const classes = useStyles();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  
  // State management
  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [tab, setTab] = useState(0);
  const [period, setPeriod] = useState(0);
  const { find } = useDashboard();

  // Filter states
  const [showFilter, setShowFilter] = useState(false);
  const [fetchDataFilter, setFetchDataFilter] = useState(false);

  // Date management
  const today = useMemo(() => new Date(), []);
  const currentMonth = useMemo(() => {
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    return `${year}-${month < 10 ? `0${month}` : `${month}`}-01`;
  }, [today]);
  const currentDate = useMemo(() => moment().format("YYYY-MM-DD"), []);
  
  const [dateStartTicket, setDateStartTicket] = useState(currentMonth);
  const [dateEndTicket, setDateEndTicket] = useState(currentDate);
  const [queueTicket, setQueueTicket] = useState('');
  const [userTicket, setUserTicket] = useState('');

  const { user } = useContext(AuthContext);
  
  // Move hooks to the top level
  const contactsCountAll = useContacts({});
  const contactsCountFiltered = useContacts({
    dateStart: dateStartTicket,
    dateEnd: dateEndTicket,
  });
  
  const messagesReceivedAll = useMessages({ fromMe: false });
  const messagesReceivedFiltered = useMessages({
    fromMe: false,
    dateStart: dateStartTicket,
    dateEnd: dateEndTicket,
  });
  
  const messagesSentAll = useMessages({ fromMe: true });
  const messagesSentFiltered = useMessages({
    fromMe: true,
    dateStart: dateStartTicket,
    dateEnd: dateEndTicket,
  });

  // Helper functions (moved outside of useMemo dependencies)
  const formatTime = useCallback((minutes) => {
    return moment().startOf("day").add(minutes, "minutes").format("HH[h] mm[m]");
  }, []);

  const getUsers = useCallback(() => {
    let userOnline = 0;
    attendants.forEach((user) => {
      if (user.online === true) {
        userOnline = userOnline + 1;
      }
    });
    return userOnline === 0 ? 0 : userOnline;
  }, [attendants]);

  const getMessages = useCallback((all, fromMe) => {
    if (fromMe) {
      return all ? messagesSentAll.count : messagesSentFiltered.count;
    } else {
      return all ? messagesReceivedAll.count : messagesReceivedFiltered.count;
    }
  }, [messagesSentAll.count, messagesSentFiltered.count, messagesReceivedAll.count, messagesReceivedFiltered.count]);

  // Memoized metrics data
  const metricsData = useMemo(() => [
    {
      label: i18n.t("dashboard.cards.inAttendance"),
      value: counters.supportHappening || 0,
      icon: <CallIcon />,
      color: '#00BCD4',
      id: 'supportHappening'
    },
    {
      label: i18n.t("dashboard.cards.waiting"),
      value: counters.supportPending || 0,
      icon: <HourglassEmptyIcon />,
      color: '#FF9800',
      id: 'supportPending'
    },
    {
      label: i18n.t("dashboard.cards.finalized"),
      value: counters.supportFinished || 0,
      icon: <CheckCircleIcon />,
      color: '#4CAF50',
      id: 'supportFinished'
    },
    {
      label: i18n.t("dashboard.cards.groups"),
      value: counters.supportGroups || 0,
      icon: <Groups />,
      color: '#9C27B0',
      id: 'supportGroups'
    },
    {
      label: i18n.t("dashboard.cards.activeAttendants"),
      value: `${getUsers()}/${attendants.length}`,
      icon: <RecordVoiceOverIcon />,
      color: '#795548',
      id: 'activeAttendants'
    },
    {
      label: i18n.t("dashboard.cards.newContacts"),
      value: counters.leads || 0,
      icon: <GroupAddIcon />,
      color: '#607D8B',
      id: 'leads'
    },
    {
      label: i18n.t("dashboard.cards.totalReceivedMessages"),
      value: `${getMessages(false, false)}/${getMessages(true, false)}`,
      icon: <MessageIcon />,
      color: '#3F51B5',
      id: 'receivedMessages'
    },
    {
      label: i18n.t("dashboard.cards.totalSentMessages"),
      value: `${getMessages(false, true)}/${getMessages(true, true)}`,
      icon: <SendIcon />,
      color: '#4CAF50',
      id: 'sentMessages'
    },
    {
      label: i18n.t("dashboard.cards.averageServiceTime"),
      value: formatTime(counters.avgSupportTime),
      icon: <AccessAlarmIcon />,
      color: '#FF5722',
      id: 'avgSupportTime'
    },
    {
      label: i18n.t("dashboard.cards.averageWaitingTime"),
      value: formatTime(counters.avgWaitTime),
      icon: <TimerIcon />,
      color: '#E91E63',
      id: 'avgWaitTime'
    },
    {
      label: i18n.t("dashboard.cards.activeTickets"),
      value: counters.activeTickets || 0,
      icon: <ArrowUpward />,
      color: '#F44336',
      id: 'activeTickets'
    },
    {
      label: i18n.t("dashboard.cards.passiveTickets"),
      value: counters.passiveTickets || 0,
      icon: <ArrowDownward />,
      color: '#4CAF50',
      id: 'passiveTickets'
    },
    {
      label: i18n.t("dashboard.cards.firstContactResolution"),
      value: `${(counters.firstContactResolution || 0).toFixed(1)}%`,
      icon: <CheckCircleIcon />,
      color: '#00C853',
      id: 'fcr'
    },
    {
      label: i18n.t("dashboard.cards.avgFirstResponseTime"),
      value: formatTime(counters.avgFirstResponseTime),
      icon: <SpeedIcon />,
      color: '#2979FF',
      id: 'frt'
    },
    {
      label: i18n.t("dashboard.cards.abandonmentRate"),
      value: `${(counters.abandonmentRate || 0).toFixed(1)}%`,
      icon: <WarningIcon />,
      color: '#D32F2F',
      id: 'abandonment'
    },
    {
      label: i18n.t("dashboard.cards.reopenRate"),
      value: `${(counters.reopenRate || 0).toFixed(1)}%`,
      icon: <LoopIcon />,
      color: '#F57C00',
      id: 'reopen'
    },
  ], [counters, attendants.length, getUsers, getMessages, formatTime]);
  
  // Memoized NPS data
  const npsData = useMemo(() => [
    {
      label: 'Score',
      value: counters.npsScore || 0,
      color: '#2196F3'
    },
    {
      label: 'Promotores',
      value: counters.npsPromotersPerc || 0,
      color: '#4CAF50'
    },
    {
      label: 'Neutros',
      value: counters.npsPassivePerc || 0,
      color: '#FF9800'
    },
    {
      label: 'Detratores',
      value: counters.npsDetractorsPerc || 0,
      color: '#F44336'
    },
  ], [counters]);
  
  // Memoized attendance data
  const attendanceData = useMemo(() => [
    {
      label: i18n.t("dashboard.assessments.totalCalls"),
      value: counters.tickets || 0,
      icon: <CallIcon />,
      color: '#00BCD4'
    },
    {
      label: i18n.t("dashboard.assessments.callsWaitRating"),
      value: counters.waitRating || 0,
      icon: <HourglassEmptyIcon />,
      color: '#FF9800'
    },
    {
      label: i18n.t("dashboard.assessments.callsWithoutRating"),
      value: counters.withoutRating || 0,
      icon: <VisibilityOffIcon />,
      color: '#F44336'
    },
    {
      label: i18n.t("dashboard.assessments.ratedCalls"),
      value: counters.withRating || 0,
      icon: <VisibilityIcon />,
      color: '#4CAF50'
    },
  ], [counters]);

  // Optimized Excel export function
  const exportarGridParaExcel = useCallback(() => {
    try {
      const ws = XLSX.utils.table_to_sheet(
        document.getElementById("grid-attendants")
      );
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "RelatorioDeAtendentes");
      XLSX.writeFile(wb, "relatorio-de-atendentes.xlsx");
      toastUtils.success('Relatório exportado com sucesso!', 'Dashboard Atualizado');
    } catch (error) {
      toastUtils.error('Erro ao exportar relatório', 'Erro nos Dados');
    }
  }, []);

  // Comprehensive dashboard export function
  const exportarDashboardCompleto = useCallback(() => {
    try {
      const wb = XLSX.utils.book_new();

      // Sheet 1: Métricas Gerais
      const metricsSheet = [
        ['Métrica', 'Valor'],
        ['Em Atendimento', counters.supportHappening || 0],
        ['Aguardando', counters.supportPending || 0],
        ['Finalizados', counters.supportFinished || 0],
        ['Grupos', counters.supportGroups || 0],
        ['Novos Contatos (Leads)', counters.leads || 0],
        ['Tickets Ativos', counters.activeTickets || 0],
        ['Tickets Passivos', counters.passiveTickets || 0],
        ['Total de Tickets', counters.tickets || 0],
        ['T.M. de Atendimento (min)', (counters.avgSupportTime || 0).toFixed(2)],
        ['T.M. de Espera (min)', (counters.avgWaitTime || 0).toFixed(2)],
        ['T.M. Primeira Resposta (min)', (counters.avgFirstResponseTime || 0).toFixed(2)],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(metricsSheet);
      XLSX.utils.book_append_sheet(wb, ws1, 'Métricas Gerais');

      // Sheet 2: Métricas de Qualidade
      const qualitySheet = [
        ['Métrica', 'Valor'],
        ['Resolução no 1º Contato (%)', (counters.firstContactResolution || 0).toFixed(2)],
        ['Taxa de Abandono (%)', (counters.abandonmentRate || 0).toFixed(2)],
        ['Taxa de Reabertura (%)', (counters.reopenRate || 0).toFixed(2)],
        ['Total de Atendimentos', counters.tickets || 0],
        ['Aguardando Avaliação', counters.waitRating || 0],
        ['Sem Avaliação', counters.withoutRating || 0],
        ['Com Avaliação', counters.withRating || 0],
        ['Índice de Avaliação (%)', (counters.percRating || 0).toFixed(2)],
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(qualitySheet);
      XLSX.utils.book_append_sheet(wb, ws2, 'Métricas de Qualidade');

      // Sheet 3: NPS
      const npsSheet = [
        ['Métrica NPS', 'Valor'],
        ['NPS Score', (counters.npsScore || 0).toFixed(2)],
        ['Promotores (%)', (counters.npsPromotersPerc || 0).toFixed(2)],
        ['Neutros (%)', (counters.npsPassivePerc || 0).toFixed(2)],
        ['Detratores (%)', (counters.npsDetractorsPerc || 0).toFixed(2)],
      ];
      const ws3 = XLSX.utils.aoa_to_sheet(npsSheet);
      XLSX.utils.book_append_sheet(wb, ws3, 'NPS');

      // Sheet 4: Tickets por Canal
      if (counters.ticketsByChannel && Object.keys(counters.ticketsByChannel).length > 0) {
        const channelsSheet = [['Canal', 'Quantidade']];
        Object.entries(counters.ticketsByChannel).forEach(([channel, count]) => {
          channelsSheet.push([channel, count]);
        });
        const ws4 = XLSX.utils.aoa_to_sheet(channelsSheet);
        XLSX.utils.book_append_sheet(wb, ws4, 'Tickets por Canal');
      }

      // Sheet 5: Atendentes
      if (attendants && attendants.length > 0) {
        const attendantsSheet = [
          ['Nome', 'Online', 'Tickets', 'T.M. Espera (min)', 'T.M. Atendimento (min)', 'Avaliação Média', 'Qtd. Avaliações']
        ];
        attendants.forEach(att => {
          attendantsSheet.push([
            att.name || '-',
            att.online ? 'Sim' : 'Não',
            att.tickets || 0,
            (att.avgWaitTime || 0).toFixed(2),
            (att.avgSupportTime || 0).toFixed(2),
            (att.rating || 0).toFixed(2),
            att.countRating || 0
          ]);
        });
        const ws5 = XLSX.utils.aoa_to_sheet(attendantsSheet);
        XLSX.utils.book_append_sheet(wb, ws5, 'Atendentes');
      }

      // Generate filename with date
      const filename = `dashboard-completo-${moment().format('YYYY-MM-DD-HHmm')}.xlsx`;
      XLSX.writeFile(wb, filename);

      toastUtils.success('Dashboard completo exportado com sucesso!', 'Exportação Concluída');
    } catch (error) {
      console.error('Erro ao exportar dashboard:', error);
      toastUtils.error('Erro ao exportar dashboard completo', 'Erro na Exportação');
    }
  }, [counters, attendants]);

  useEffect(() => {
    let isMounted = true;
    
    async function firstLoad() {
      if (isMounted) {
        console.log('Executando firstLoad...');
        await fetchData();
      }
    }
    
    const timeoutId = setTimeout(() => {
      firstLoad();
    }, 500);
    
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDataFilter]);

  async function fetchData() {
    setLoading(true);
    console.log('Iniciando fetchData...');

    let params = {};

    // Construir parâmetros de filtro
    if (period > 0) {
      params = {
        days: period,
      };
      console.log('Usando filtro por dias:', period);
    } else {
      // Se não há período específico, usar as datas
      if (!isEmpty(dateStartTicket) && moment(dateStartTicket).isValid()) {
        params = {
          ...params,
          date_from: moment(dateStartTicket).format("YYYY-MM-DD"),
        };
        console.log('Data de início:', dateStartTicket);
      }

      if (!isEmpty(dateEndTicket) && moment(dateEndTicket).isValid()) {
        params = {
          ...params,
          date_to: moment(dateEndTicket).format("YYYY-MM-DD"),
        };
        console.log('Data de fim:', dateEndTicket);
      }
    }

    // Adicionar filtro por fila
    if (queueTicket && queueTicket !== '') {
      params = {
        ...params,
        queueId: queueTicket,
      };
      console.log('Filtro por fila:', queueTicket);
    }

    // Adicionar filtro por atendente
    if (userTicket && userTicket !== '') {
      params = {
        ...params,
        userId: userTicket,
      };
      console.log('Filtro por atendente:', userTicket);
    }

    // Se nenhum parâmetro foi definido, usar período padrão de 30 dias
    if (Object.keys(params).length === 0) {
      console.log('Nenhum filtro definido, usando 30 dias como padrão');
      params = { days: 30 };
    }

    console.log('Parâmetros finais para busca:', params);
  
    try {
      const data = await find(params);
      console.log('Dados recebidos no componente:', data);
  
      // Verificar se data é um objeto de erro do Axios
      if (data && typeof data === 'object' && data.message && data.name && data.code) {
        // É um objeto de erro, definir valores padrão
        console.error('Erro ao buscar dados:', data);
        toastUtils.error('Erro ao carregar dados do dashboard', 'Erro nos Dados');
        
        setCounters({
          avgSupportTime: 0,
          avgWaitTime: 0,
          supportFinished: 0,
          supportHappening: 0,
          supportPending: 0,
          supportGroups: 0,
          leads: 0,
          activeTickets: 0,
          passiveTickets: 0,
          tickets: 0,
          waitRating: 0,
          withoutRating: 0,
          withRating: 0,
          percRating: 0,
          npsPromotersPerc: 0,
          npsPassivePerc: 0,
          npsDetractorsPerc: 0,
          npsScore: 0
        });
        setAttendants([]);
        return;
      }
      
      // Garantir que counters sempre tenha valores válidos
      const safeCounters = data.counters || {};
      
      setCounters(safeCounters);
      
      if (isArray(data.attendants)) {
        setAttendants(data.attendants);
      } else {
        console.warn('Attendants não é um array:', data.attendants);
        setAttendants([]);
      }
      
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      toastUtils.error('Erro ao carregar dados do dashboard', 'Erro nos Dados');
      
      // Definir valores padrão em caso de erro
      setCounters({
        avgSupportTime: 0,
        avgWaitTime: 0,
        supportFinished: 0,
        supportHappening: 0,
        supportPending: 0,
        supportGroups: 0,
        leads: 0,
        activeTickets: 0,
        passiveTickets: 0,
        tickets: 0,
        waitRating: 0,
        withoutRating: 0,
        withRating: 0,
        percRating: 0,
        npsPromotersPerc: 0,
        npsPassivePerc: 0,
        npsDetractorsPerc: 0,
        npsScore: 0
      });
      setAttendants([]);
    } finally {
      setLoading(false);
    }
  }

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    setSelectedUsers(users);
  };

  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
  };

  function toggleShowFilter() {
    setShowFilter(!showFilter);
  }

  return (
    <ErrorBoundary>
      <MainContainer>
        {user.profile === "user" && user.showDashboard === "disabled" ? (
          <ForbiddenPage />
        ) : (
        <Box className={classes.root}>
          <Paper className={classes.mainPaper} variant="outlined">
            <Container maxWidth={false} className={classes.container}>
              <Grid container spacing={2} style={{ margin: 0, width: '100%' }}>
                {/* FILTROS */}
                <Grid item xs={12} container justifyContent="flex-end" spacing={1}>
                  <Grid item>
                    <Button
                      onClick={toggleShowFilter}
                      color="primary"
                      startIcon={!showFilter ? <FilterListIcon /> : <ClearIcon />}
                    >
                      {showFilter ? "Ocultar Filtros" : "Mostrar Filtros"}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      onClick={exportarDashboardCompleto}
                      variant="outlined"
                      color="primary"
                      disabled={loading}
                      startIcon={<AssessmentIcon />}
                    >
                      {i18n.t("dashboard.buttons.export")}
                    </Button>
                  </Grid>
                </Grid>

                {showFilter && (
                  <Grid item xs={12} style={{ marginBottom: "20px" }}>
                    <Filters
                      classes={classes}
                      setDateStartTicket={setDateStartTicket}
                      setDateEndTicket={setDateEndTicket}
                      dateStartTicket={dateStartTicket}
                      dateEndTicket={dateEndTicket}
                      setQueueTicket={setQueueTicket}
                      queueTicket={queueTicket}
                      setUserTicket={setUserTicket}
                      userTicket={userTicket}
                      attendants={attendants}
                      fetchData={setFetchDataFilter}
                      period={period}
                      setPeriod={setPeriod}
                    />
                  </Grid>
                )}

                {/* Indicadores Gerais - Reorganizados por Categoria */}
                <Grid item xs={12} style={{ marginTop: '20px' }}>
                  <Box className={classes.sectionHeader}>
                    <Typography className={classes.sectionTitle}>
                      <AssessmentIcon />
                      {i18n.t("dashboard.sections.indicators")}
                    </Typography>
                  </Box>
                </Grid>

                {/* Server Metrics Card - Comentado temporariamente para diagnóstico */}
                {/* <Grid item xs={12} md={6} lg={4}>
                  <ServerMetricsCard />
                </Grid> */}

                {/* Seção: Atendimento em Tempo Real */}
                <Grid item xs={12} style={{ marginTop: '10px' }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                    {i18n.t("dashboard.sections.realTime")}
                  </Typography>
                </Grid>
                {metricsData.slice(0, 4).map((metric, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={metric.id}>
                    <MetricCard
                      icon={metric.icon}
                      value={metric.value}
                      label={metric.label}
                      color={metric.color}
                      loading={loading}
                    />
                  </Grid>
                ))}

                {/* Server Metrics Card - Comentado temporariamente (duplicado) */}
                {/* <Grid item xs={12} style={{ marginTop: '20px' }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                    Métricas do Sistema
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <ServerMetricsCard />
                </Grid> */}

                {/* Seção: Métricas de Desempenho */}
                <Grid item xs={12} style={{ marginTop: '20px' }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                    {i18n.t("dashboard.sections.performance")}
                  </Typography>
                </Grid>
                {metricsData.slice(4, 12).map((metric, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={metric.id}>
                    <MetricCard
                      icon={metric.icon}
                      value={metric.value}
                      label={metric.label}
                      color={metric.color}
                      loading={loading}
                    />
                  </Grid>
                ))}

                {/* Seção: Métricas Críticas de Qualidade */}
                <Grid item xs={12} style={{ marginTop: '20px' }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                    {i18n.t("dashboard.sections.quality")}
                  </Typography>
                </Grid>
                {metricsData.slice(12).map((metric, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={metric.id}>
                    <MetricCard
                      icon={metric.icon}
                      value={metric.value}
                      label={metric.label}
                      color={metric.color}
                      loading={loading}
                    />
                  </Grid>
                ))}

                {/* Lista de Empresas - Apenas para user.id === 1 */}
                {user.id === 1 && (
                  <Grid item xs={12}>
                    <CompaniesList />
                  </Grid>
                )}

                {/* NPS Section */}
                <Grid item xs={12}>
                  <Box className={classes.section}>
                    <Box className={classes.sectionHeader}>
                      <Typography className={classes.sectionTitle}>
                        <TrendingUpIcon />
                        Pesquisa de Satisfação (NPS)
                      </Typography>
                    </Box>
                    
                    <Grid container spacing={isMdUp ? 3 : 2}>
                      {npsData.map((nps, index) => (
                        <Grid item xs={6} md={3} key={index}>
                          <ProgressCard
                            label={nps.label}
                            value={nps.value}
                            color={nps.color}
                            loading={loading}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Grid>

                {/* Attendance Information Section */}
                <Grid item xs={12}>
                  <Box className={classes.section}>
                    <Box className={classes.sectionHeader}>
                      <Typography className={classes.sectionTitle}>
                        <CallIcon />
                        Informações de Atendimento
                      </Typography>
                    </Box>
                    
                    <Grid container spacing={isMdUp ? 3 : 2}>
                      {attendanceData.map((attendance, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <MetricCard
                            icon={attendance.icon}
                            value={attendance.value}
                            label={attendance.label}
                            color={attendance.color}
                            loading={loading}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Grid>

                {/* Rating Index Section */}
                <Grid item xs={12}>
                  <Box className={classes.section}>
                    <Box className={classes.sectionHeader}>
                      <Typography className={classes.sectionTitle}>
                        <AssessmentIcon />
                        {i18n.t("dashboard.assessments.evaluationIndex")}
                      </Typography>
                    </Box>
                    
                    <Card className={classes.modernCard}>
                      <CardContent className={classes.cardContent}>
                        <Grid container alignItems="center" spacing={3}>
                          <Grid item xs={12} sm={3} md={2}>
                            <Box className={classes.centerContent}>
                              <Box
                                sx={{
                                  padding: 2,
                                  borderRadius: 2,
                                  backgroundColor: '#FFE3B3',
                                  minWidth: 80,
                                }}
                              >
                                <Typography 
                                  variant="h4" 
                                  sx={{ 
                                    color: '#F79009', 
                                    fontWeight: 700,
                                    textAlign: 'center'
                                  }}
                                >
                                  {loading ? (
                                    <Skeleton width="100%" height={40} />
                                  ) : (
                                    `${(counters.percRating || 0).toFixed(1)}%`
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={9} md={10}>
                            {loading ? (
                              <Skeleton height={12} sx={{ borderRadius: 1 }} />
                            ) : (
                              <LinearProgress
                                variant="determinate"
                                value={counters.percRating || 0}
                                sx={{
                                  height: 12,
                                  borderRadius: 1,
                                  backgroundColor: theme.palette.action.hover,
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: '#F79009',
                                    borderRadius: 1,
                                  },
                                }}
                              />
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Box>
                </Grid>

                {/* Attendants Table Section */}
                <Grid item xs={12}>
                  <Box className={classes.section}>
                    <Box className={classes.sectionHeader}>
                      <Typography className={classes.sectionTitle}>
                        <Groups />
                        Atendentes
                      </Typography>
                      {!loading && attendants.length > 0 && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={exportarGridParaExcel}
                          className={classes.mobileHidden}
                          startIcon={<AssessmentIcon />}
                        >
                          Exportar
                        </Button>
                      )}
                    </Box>
                    
                    <Card className={classes.chartContainer}>
                      <TableAttendantsStatus
                        attendants={attendants}
                        loading={loading}
                      />
                    </Card>
                  </Box>
                </Grid>

                {/* Charts Section */}
                <Grid item xs={12}>
                  <Box className={classes.section}>
                    <Box className={classes.sectionHeader}>
                      <Typography className={classes.sectionTitle}>
                        <TrendingUpIcon />
                        {i18n.t("dashboard.sections.charts")}
                      </Typography>
                    </Box>

                    <Grid container spacing={isMdUp ? 3 : 2}>
                      <Grid item xs={12}>
                        <Card className={classes.chartContainer}>
                          <Suspense fallback={<ChartLoadingFallback />}>
                            <ChartsTimeline />
                          </Suspense>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Card className={classes.chartContainer}>
                          <Suspense fallback={<ChartLoadingFallback />}>
                            <ChatsUser />
                          </Suspense>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Card className={classes.chartContainer}>
                          <Suspense fallback={<ChartLoadingFallback />}>
                            <ChartsDate />
                          </Suspense>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Card className={classes.chartContainer}>
                          <Suspense fallback={<ChartLoadingFallback />}>
                            <ChartsPieStatus counters={counters} />
                          </Suspense>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Card className={classes.chartContainer}>
                          <Suspense fallback={<ChartLoadingFallback />}>
                            <ChartsNPSGauge counters={counters} />
                          </Suspense>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Card className={classes.chartContainer}>
                          <Suspense fallback={<ChartLoadingFallback />}>
                            <ChartsChannels counters={counters} />
                          </Suspense>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Paper>
        </Box>
        )}
      </MainContainer>
    </ErrorBoundary>
  );
};

// Add display names for better debugging
MetricCard.displayName = 'MetricCard';
ProgressCard.displayName = 'ProgressCard';
Dashboard.displayName = 'Dashboard';

export default Dashboard;